import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlaceIcon from '@mui/icons-material/PlaceOutlined';
import SpeedIcon from '@mui/icons-material/Speed';
import SwapHorizOutlined from '@mui/icons-material/SwapHorizOutlined';
import { Button, Stack, StackProps, Typography } from '@mui/material';

import { Device } from '@/api/devices';
import { useScoutLatestData } from '@/api/scout';
import { Null } from '@/components/Null';
import { useTetherDeviceIdentity } from '@/hooks/useTetherDeviceIdentity';
import { boldWeight, cardBgLightGrey } from '@/theme';
import { calculateDeviceLocation } from '@/utils';
import { MAX_SCOUT_BATTERY_LEVEL, getScoutBatteryStatus, getScoutDisplayLabel } from '@/utils/scoutStatusHelpers';

import { DataReadoutGrid, DataReadoutGridProps } from '../data/DataReadoutGrid';
import { Datum } from '../data/Datum';
import { DirectionDatum } from '../data/DirectionDatum';
import { DotLevelIndicator } from '../data/DotLevelIndicator';
import { IconData, LabeledData } from '../data/IconData';
import { LatLong } from '../data/LatLong';
import { GenericSkeleton } from '../loaders/GenericSkeleton';

const SCOUT_SECTION_SX = {
    bgcolor: cardBgLightGrey,
    borderTop: '1px solid rgba(192, 192, 192, 0.8)',
    mx: -1,
    mb: -1,
    mt: 1.5,
    px: 1,
    py: 1.5,
};

const SCOUT_DATA_READOUT_SX = { padding: 1.5, backgroundColor: 'white', border: 'unset' };

export const ScoutSummarySection = ({
    dataColumnCount,
    device,
    ...props
}: {
    device: Device;
    dataColumnCount?: DataReadoutGridProps['columns'];
} & StackProps) => {
    const { isScout, spotterId, tetherDashboardUrl } = useTetherDeviceIdentity({ spotterId: device.spotterId });
    const { data: scoutLatestData, isPending: isScoutDataPending } = useScoutLatestData({
        spotterId,
        config: { enabled: isScout },
    });
    const scoutBatteryStatus = getScoutBatteryStatus(scoutLatestData);
    const scoutDisplayLabel = getScoutDisplayLabel(scoutLatestData);

    if (!isScout) {
        return <Null data-testid="scout-summary-not-scout" />;
    }
    if (isScoutDataPending) {
        return <GenericSkeleton />;
    }
    if (!scoutLatestData) {
        return null;
    }

    const scoutTimestamp = scoutLatestData.timestamp ?? undefined;
    const scoutPosition = scoutLatestData.position;
    const reconciledScoutLocation = calculateDeviceLocation({
        spotterTrack: device.track,
        scoutLocation: scoutLatestData,
    });
    const reconciledScoutTimestamp = reconciledScoutLocation?.timestamp ?? undefined;
    const scoutHeading = scoutPosition?.heading_deg;

    return (
        <Stack sx={SCOUT_SECTION_SX}>
            <Stack direction="row" justifyContent="space-between" gap={0.5} sx={{ mb: 1 }}>
                <Typography variant="h4" alignContent="center">
                    Scout
                </Typography>
                <Stack direction="row" alignItems="center" gap={0.5}>
                    <SwapHorizOutlined />
                    {scoutDisplayLabel && (
                        <Typography variant="minorLabel" color="textPrimary" sx={{ fontWeight: boldWeight, mr: 0.5 }}>
                            {scoutDisplayLabel}
                        </Typography>
                    )}
                    <Button
                        variant="contained"
                        href={tetherDashboardUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ px: 1.5, py: 0.75, textWrap: 'nowrap' }}
                        data-testid="manage-on-tether-cta"
                    >
                        Manage on Tether <OpenInNewIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                    </Button>
                </Stack>
            </Stack>
            <DataReadoutGrid {...props} columns={dataColumnCount}>
                {scoutPosition && (
                    <IconData
                        Icon={SpeedIcon}
                        label="Speed • SCOUT"
                        timestamp={scoutTimestamp}
                        sx={SCOUT_DATA_READOUT_SX}
                        data-testid="scout-summary-speed-readout"
                    >
                        <Datum value={scoutPosition.speed_ms} sourceUnit="m/s" type="speed" maxDecimalPlaces={1} />
                    </IconData>
                )}
                {scoutHeading != null && (
                    <LabeledData
                        label="Heading • SCOUT"
                        timestamp={scoutTimestamp}
                        sx={{ ...SCOUT_DATA_READOUT_SX, justifyContent: 'space-between' }}
                        data-testid="scout-summary-heading-readout"
                    >
                        <DirectionDatum type="flow" heading={scoutHeading} tooltip="Heading" />
                    </LabeledData>
                )}
                {reconciledScoutLocation && (
                    <IconData
                        Icon={PlaceIcon}
                        label="Location • SCOUT"
                        timestamp={reconciledScoutTimestamp}
                        sx={SCOUT_DATA_READOUT_SX}
                        data-testid="scout-summary-location-readout"
                    >
                        <LatLong coord={reconciledScoutLocation} />
                    </IconData>
                )}
                {scoutBatteryStatus && (
                    <IconData
                        Icon={scoutBatteryStatus.StatusIcon}
                        label="Battery • SCOUT"
                        sx={SCOUT_DATA_READOUT_SX}
                        timestamp={scoutBatteryStatus.timestamp}
                        data-testid="scout-summary-battery-readout"
                    >
                        {scoutBatteryStatus?.level && (
                            <DotLevelIndicator
                                level={scoutBatteryStatus.level}
                                maxLevel={MAX_SCOUT_BATTERY_LEVEL}
                                color={scoutBatteryStatus.color}
                            />
                        )}
                    </IconData>
                )}
            </DataReadoutGrid>
        </Stack>
    );
};
